import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  Car, LogOut, Calendar, MapPin, User, Clock, Phone, Plane, 
  CheckCircle, Navigation, MessageSquare, CalendarPlus, Download, Wifi, WifiOff,
  DollarSign, Wallet, XCircle, Check, X as XIcon, Maximize, Power
} from "lucide-react";
import useWebSocket from "../hooks/useWebSocket";
import { DriverStatusUpdater, DriverGPSSharing } from "../components/DriverTracking";
import { DashboardSkeleton } from "../components/DashboardSkeleton";
import NotificationBell from "../components/NotificationBell";

const DriverDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("today");
  const [activeJobId, setActiveJobId] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [greetingJob, setGreetingJob] = useState(null);
  const [acceptingId, setAcceptingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [availabilityStatus, setAvailabilityStatus] = useState("offline");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const handleWebSocketMessage = useCallback((message) => {
    if (message.type === "booking_dispatched" && message.data.driver_id === user?.id) {
      toast.info(`New job assigned: ${message.data.booking_ref}`, { description: message.data.pickup_location });
      fetchBookings();
    } else if (message.type === "booking_update" || message.type === "booking_status_update") {
      setBookings(prev => {
        const index = prev.findIndex(b => b.id === message.data.id);
        if (index >= 0) { const updated = [...prev]; updated[index] = message.data; return updated; }
        return prev;
      });
    }
  }, [user?.id]);

  const { isConnected } = useWebSocket(handleWebSocketMessage);

  useEffect(() => { fetchBookings(); fetchEarnings(); fetchAvailabilityStatus(); }, []);

  const fetchAvailabilityStatus = async () => {
    try {
      const response = await api.get("/driver-availability/my-status");
      setAvailabilityStatus(response.data.status || "offline");
    } catch (error) { console.error("Failed to load availability status"); }
  };

  const toggleAvailability = async () => {
    const newStatus = availabilityStatus === "online" ? "offline" : "online";
    setUpdatingStatus(true);
    try {
      await api.put("/driver-availability/status", { status: newStatus });
      setAvailabilityStatus(newStatus);
      toast.success(newStatus === "online" ? "You are now online and available for jobs" : "You are now offline");
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await api.get("/driver/my-jobs");
      setBookings(response.data);
    } catch (error) { toast.error("Failed to load jobs"); }
    finally { setLoading(false); }
  };

  const fetchEarnings = async () => {
    try {
      const response = await api.get("/driver/earnings");
      setEarnings(response.data);
    } catch (error) { console.error("Failed to load earnings"); }
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  const handleCompleteJob = async (bookingId) => {
    try {
      await api.post(`/bookings/${bookingId}/complete`);
      toast.success("Job completed");
      fetchBookings();
      setActiveJobId(null);
    } catch (error) { toast.error("Failed to complete job"); }
  };

  const handleAcceptJob = async (bookingId) => {
    setAcceptingId(bookingId);
    try {
      await api.post(`/driver/jobs/${bookingId}/accept`);
      toast.success("Job accepted");
      fetchBookings();
    } catch (error) { toast.error("Failed to accept job"); }
    finally { setAcceptingId(null); }
  };

  const handleRejectJob = async (bookingId) => {
    if (!window.confirm("Are you sure you want to reject this job? It will be returned to dispatch.")) return;
    setRejectingId(bookingId);
    try {
      await api.post(`/driver/jobs/${bookingId}/reject`);
      toast.success("Job rejected — returned to dispatch");
      fetchBookings();
    } catch (error) { toast.error("Failed to reject job"); }
    finally { setRejectingId(null); }
  };

  const getStatusBadge = (status) => {
    const map = {
      dispatched: { bg: "bg-purple-500/15 text-purple-400 border-purple-500/20", label: "Dispatched" },
      in_progress: { bg: "bg-blue-500/15 text-blue-400 border-blue-500/20", label: "In Progress" },
      completed: { bg: "bg-green-500/15 text-green-400 border-green-500/20", label: "Completed" },
      cancelled: { bg: "bg-red-500/15 text-red-400 border-red-500/20", label: "Cancelled" },
      confirmed: { bg: "bg-blue-500/15 text-blue-400 border-blue-500/20", label: "Confirmed" },
      pending: { bg: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20", label: "Pending" },
    };
    const s = map[status] || { bg: "bg-gray-500/15 text-gray-400", label: status };
    return <Badge className={`${s.bg} border text-[11px] font-medium`}>{s.label}</Badge>;
  };

  const today = new Date().toDateString();
  const todayJobs = bookings.filter(b => new Date(b.pickup_datetime).toDateString() === today && b.status !== "completed" && b.status !== "cancelled");
  const upcomingJobs = bookings.filter(b => new Date(b.pickup_datetime) > new Date() && b.status !== "completed" && b.status !== "cancelled");
  const completedJobs = bookings.filter(b => b.status === "completed");
  const activeJobs = activeTab === "today" ? todayJobs : activeTab === "upcoming" ? upcomingJobs : completedJobs;

  if (loading) return <DashboardSkeleton role="driver" />;

  return (
    <div className="min-h-screen bg-[#0b0b0f] pb-20 text-white" data-testid="driver-dashboard">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#111114] border-b border-white/[0.06]">
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-7 w-auto" />
            <div className="flex items-center gap-3">
              {/* Availability Toggle */}
              <button
                onClick={toggleAvailability}
                disabled={updatingStatus}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  availabilityStatus === "online"
                    ? "bg-green-500/15 text-green-400 border border-green-500/30"
                    : "bg-white/[0.04] text-white/40 border border-white/[0.06]"
                }`}
                data-testid="availability-toggle"
              >
                <Power className={`h-3.5 w-3.5 ${updatingStatus ? 'animate-pulse' : ''}`} />
                {availabilityStatus === "online" ? "Online" : "Offline"}
              </button>
              <NotificationBell onNotificationClick={(n) => {
                if (n.data?.booking_id) {
                  const job = bookings.find(b => b.id === n.data.booking_id);
                  if (job) setActiveJobId(job.id);
                }
              }} />
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-medium ${isConnected ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                {isConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                {isConnected ? 'Live' : 'Offline'}
              </div>
              <div className="w-7 h-7 rounded-full bg-[#C8A95B]/15 flex items-center justify-center">
                <User className="h-3.5 w-3.5 text-[#C8A95B]" />
              </div>
              <button onClick={handleLogout} className="text-white/30 hover:text-red-400 transition-colors" data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-18 px-4 max-w-lg mx-auto" style={{ paddingTop: "72px" }}>
        {/* Welcome */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-white">Hello, {user?.name?.split(' ')[0]}</h1>
          <p className="text-white/40 text-sm">{todayJobs.length} job{todayJobs.length !== 1 ? 's' : ''} today</p>
        </div>

        {/* Earnings */}
        {earnings && (
          <div className="mb-6 p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]" data-testid="earnings-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center">
                <Wallet className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-[10px] text-white/30 uppercase tracking-wider font-medium">Total Earnings</p>
                <p className="text-2xl font-bold text-green-400">${earnings.total_earnings?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 p-3 rounded-lg bg-white/[0.03]">
              <div className="text-center">
                <p className="text-[10px] text-white/30">Completed</p>
                <p className="font-bold text-white/80">{earnings.completed_jobs || 0}</p>
              </div>
              <div className="text-center border-x border-white/[0.06]">
                <p className="text-[10px] text-white/30">Pending</p>
                <p className="font-bold text-[#C8A95B]">${earnings.pending_earnings?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-white/30">Total</p>
                <p className="font-bold text-white/80">${earnings.total_earnings?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "today", label: "Today", count: todayJobs.length },
            { id: "upcoming", label: "Upcoming", count: upcomingJobs.length },
            { id: "completed", label: "Completed", count: completedJobs.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? "bg-[#C8A95B] text-black" 
                  : "bg-white/[0.04] text-white/40 hover:bg-white/[0.06]"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Jobs */}
        <div className="space-y-3">
          {activeJobs.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="h-10 w-10 text-white/10 mx-auto mb-3" />
              <p className="text-white/30 text-sm">No {activeTab} jobs</p>
            </div>
          ) : (
            activeJobs.map(job => (
              <div key={job.id} className="rounded-xl bg-white/[0.03] border border-white/[0.06] overflow-hidden" data-testid={`job-card-${job.id}`}>
                {/* Status color bar */}
                <div className={`h-0.5 ${
                  job.status === 'dispatched' ? 'bg-purple-500' :
                  job.status === 'in_progress' ? 'bg-blue-500' :
                  job.status === 'completed' ? 'bg-green-500' :
                  'bg-yellow-500'
                }`} />
                
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="font-mono text-[#C8A95B] font-medium text-sm">{job.booking_ref}</span>
                      <div className="mt-1">{getStatusBadge(job.status)}</div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/30">{new Date(job.pickup_datetime).toLocaleDateString()}</p>
                      <p className="text-lg font-bold text-[#C8A95B]">
                        {new Date(job.pickup_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>

                  {/* Passenger */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] mb-4">
                    <div className="w-9 h-9 rounded-full bg-[#C8A95B]/15 flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-[#C8A95B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-white/90">{job.passenger_name}</p>
                      <div className="flex items-center gap-1 text-xs text-white/40">
                        <Phone className="h-3 w-3" />
                        <a href={`tel:${job.passenger_phone}`} className="hover:text-[#C8A95B]">{job.passenger_phone}</a>
                      </div>
                    </div>
                    <a href={`tel:${job.passenger_phone}`} className="w-9 h-9 rounded-full bg-green-500/15 flex items-center justify-center hover:bg-green-500/25 transition-colors">
                      <Phone className="h-4 w-4 text-green-400" />
                    </a>
                  </div>

                  {/* Route */}
                  <div className="space-y-2.5 mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-md bg-green-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin className="h-3.5 w-3.5 text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-white/25 uppercase tracking-wider">Pickup</p>
                        <p className="text-sm text-white/80">{job.pickup_location}</p>
                      </div>
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.pickup_location)}`} target="_blank" rel="noopener noreferrer"
                        className="w-7 h-7 rounded-md bg-[#C8A95B]/10 flex items-center justify-center hover:bg-[#C8A95B]/20 transition-colors flex-shrink-0">
                        <Navigation className="h-3.5 w-3.5 text-[#C8A95B]" />
                      </a>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-md bg-red-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin className="h-3.5 w-3.5 text-red-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-white/25 uppercase tracking-wider">Dropoff</p>
                        <p className="text-sm text-white/80">{job.dropoff_location}</p>
                      </div>
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.dropoff_location)}`} target="_blank" rel="noopener noreferrer"
                        className="w-7 h-7 rounded-md bg-[#C8A95B]/10 flex items-center justify-center hover:bg-[#C8A95B]/20 transition-colors flex-shrink-0">
                        <Navigation className="h-3.5 w-3.5 text-[#C8A95B]" />
                      </a>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    <span className="px-2 py-1 rounded-md bg-white/[0.04] text-white/50 text-[11px] capitalize">{job.vehicle_type?.replace(/_/g, ' ')}</span>
                    <span className="px-2 py-1 rounded-md bg-white/[0.04] text-white/50 text-[11px]">{job.num_passengers} pax</span>
                    {job.flight_number && (
                      <span className="px-2 py-1 rounded-md bg-white/[0.04] text-white/50 text-[11px] flex items-center gap-1">
                        <Plane className="h-3 w-3" />{job.flight_number}
                      </span>
                    )}
                  </div>

                  {/* Driver Pay */}
                  {job.driver_pay !== null && job.driver_pay !== undefined && (
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/15 mb-4" data-testid={`driver-pay-${job.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-green-400" />
                          <span className="text-[10px] text-green-400 font-medium uppercase tracking-wider">Your Pay</span>
                        </div>
                        <span className="text-lg font-bold text-green-400">${job.driver_pay.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Special Requirements */}
                  {job.special_requirements && (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/15 mb-4">
                      <div className="flex items-start gap-2">
                        <MessageSquare className="h-4 w-4 text-yellow-400 mt-0.5" />
                        <div>
                          <p className="text-[10px] text-yellow-400 font-medium uppercase tracking-wider">Notes</p>
                          <p className="text-sm text-white/70">{job.special_requirements}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Calendar */}
                  <div className="flex gap-2 mb-4">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${job.id}/calendar`}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.06] text-white/50 text-xs transition-colors" data-testid={`download-ics-${job.id}`}>
                      <Download className="h-3.5 w-3.5" />Download .ics
                    </a>
                    <button onClick={async () => {
                      try { const res = await api.get(`/bookings/${job.id}/google-calendar`); window.open(res.data.google_calendar_url, '_blank'); }
                      catch (e) { toast.error("Failed to generate calendar link"); }
                    }} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#C8A95B]/10 hover:bg-[#C8A95B]/15 text-[#C8A95B] text-xs transition-colors" data-testid={`add-gcal-${job.id}`}>
                      <CalendarPlus className="h-3.5 w-3.5" />Add to Google
                    </button>
                  </div>

                  {/* Actions */}
                  {job.status === "dispatched" && !job.driver_accepted && (
                    <div className="flex gap-2 mb-3" data-testid={`accept-reject-${job.id}`}>
                      <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full font-semibold" onClick={() => handleAcceptJob(job.id)} disabled={acceptingId === job.id}>
                        {acceptingId === job.id ? <Clock className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                        Accept Job
                      </Button>
                      <Button variant="outline" className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full" onClick={() => handleRejectJob(job.id)} disabled={rejectingId === job.id}>
                        {rejectingId === job.id ? <Clock className="h-4 w-4 mr-2 animate-spin" /> : <XIcon className="h-4 w-4 mr-2" />}
                        Reject
                      </Button>
                    </div>
                  )}

                  {/* Greeting Sign Button */}
                  {(job.status === "dispatched" || job.status === "in_progress") && (
                    <Button variant="outline" className="w-full mb-3 border-white/10 text-[#C8A95B] hover:bg-[#C8A95B]/10 rounded-full" onClick={() => setGreetingJob(job)} data-testid={`greeting-btn-${job.id}`}>
                      <Maximize className="h-4 w-4 mr-2" />Greeting Sign
                    </Button>
                  )}

                  {(job.status === "dispatched" || job.status === "in_progress") && (job.driver_accepted || job.status === "in_progress") && (
                    <div className="space-y-3">
                      {activeJobId === job.id ? (
                        <>
                          <DriverGPSSharing bookingId={job.id} />
                          <DriverStatusUpdater bookingId={job.id} currentStatus={job.driver_status || "en_route"} onStatusUpdate={() => fetchBookings()} />
                          <Button variant="outline" className="w-full border-white/10 text-white/50 hover:text-white hover:bg-white/[0.04]" onClick={() => setActiveJobId(null)}>
                            Hide Controls
                          </Button>
                        </>
                      ) : (
                        <Button className="w-full bg-[#C8A95B] text-black hover:bg-[#d4b96a] rounded-full font-semibold" onClick={() => setActiveJobId(job.id)} data-testid={`start-tracking-${job.id}`}>
                          <Navigation className="h-4 w-4 mr-2" />Start Trip & Share Location
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[#111114] border-t border-white/[0.06] z-50">
        <div className="max-w-lg mx-auto flex items-center justify-around py-2.5">
          {[
            { id: "today", icon: Calendar, label: "Today" },
            { id: "upcoming", icon: Clock, label: "Upcoming" },
            { id: "completed", icon: CheckCircle, label: "History" },
          ].map(tab => (
            <button key={tab.id} className={`flex flex-col items-center gap-1 min-w-[60px] ${activeTab === tab.id ? "text-[#C8A95B]" : "text-white/25"}`}
              onClick={() => setActiveTab(tab.id)} data-testid={`bnav-${tab.id}`}>
              <tab.icon className="h-5 w-5" />
              <span className="text-[10px] font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Greeting Sign Modal */}
      {greetingJob && (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center" data-testid="greeting-sign-modal" onClick={() => setGreetingJob(null)}>
          <div className="absolute top-4 right-4">
            <button onClick={() => setGreetingJob(null)} className="text-white/30 hover:text-white transition-colors" data-testid="close-greeting">
              <XIcon className="h-8 w-8" />
            </button>
          </div>
          <div className="text-center px-8">
            <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-12 w-auto mx-auto mb-8 opacity-60" />
            <p className="text-[#C8A95B] uppercase tracking-[0.3em] text-xs font-medium mb-4">Welcome</p>
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-bold leading-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
              {greetingJob.passenger_name}
            </h1>
            {greetingJob.flight_number && (
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/[0.08]">
                <Plane className="h-4 w-4 text-[#C8A95B]" />
                <span className="text-white/60 text-sm">{greetingJob.flight_number}</span>
              </div>
            )}
            <p className="text-white/20 text-xs mt-8">Tap anywhere to close</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDashboard;
